import { flatten } from 'lodash';
import { DAIRY_PROTEIN_SYNONYMS } from './ProteinIngredients';

/**
 * Single source of truth for flaggable ingredients and their matching data.
 *
 * The enum values here must stay in sync with the `FlaggableIngredient` enum in
 * shef-api/src/food-items/FoodItem.graphql. That parity is enforced by
 * shef-api/test/food-items/flaggableIngredientParity.unit.test.ts.
 */
export enum FlaggableIngredient {
  Cilantro = 'cilantro',
  Mushrooms = 'mushrooms',
  Beef = 'beef',
  Pork = 'pork',
  Nuts = 'nuts',
  Shellfish = 'shellfish',
  Eggs = 'eggs',
  Soy = 'soy',
  Gluten = 'gluten',
  Dairy = 'dairy',
  Onions = 'onions',
  Avocado = 'avocado',
  Sesame = 'sesame',
  BellPepper = 'bellPepper',
  Garlic = 'garlic',
}

export const CILANTRO_SYNONYMS = ['cilantro', 'coriander', 'chinese parsley'];

export const MUSHROOM_SYNONYMS = [
  'mushroom',
  'mushrooms',
  'shiitake',
  'portobello',
  'cremini',
  'chanterelle',
  'morel',
  'porcini',
  'enoki',
];

export const NUTS_SYNONYMS = [
  'nut',
  'nuts',
  'almond',
  'cashew',
  'walnut',
  'pecan',
  'pistachio',
  'hazelnut',
  'macadamia',
  'peanut',
  'chestnut',
  'pignoli',
  'pinenut',
  'candlenut',
  'groundnut',
  'marcona',
  'nutella',
  'marzipan',
  'praline',
  'nougat',
  'gianduja',
  'frangipane',
  'satay',
  'pesto',
];

export const SHELLFISH_SYNONYMS = [
  'shellfish',
  'seafood',
  'shrimp',
  'crab',
  'lobster',
  'crayfish',
  'prawn',
  'clam',
  'mussel',
  'oyster',
  'scallop',
  'scampi',
  'abalone',
  'crawfish',
  'squid',
  'calamari',
  'octopus',
  'krill',
];

export const EGGS_SYNONYMS = [
  'egg',
  'eggs',
  'meringue',
  'mayonnaise',
  'mayo',
  'aioli',
  'albumin',
  'albumen',
  'caesar',
  'flan',
  'ranch',
  'salad cream',
];

export const SOY_SYNONYMS = [
  'soy',
  'soya',
  'tofu',
  'bean curd',
  'edamame',
  'tempeh',
  'soy sauce',
  'soybean',
  'soybeans',
  'miso',
  'gochujang',
  'tamari',
  'hoisin',
  'natto',
  'yuba',
  'aburaage',
  'textured vegetable protein',
  'tvp',
  'doenjang',
  'teriyaki sauce',
  'oyster sauce',
  'shoyu',
  'tonkatsu',
  'ponzu',
  'doubanjiang',
  'kecap',
  'worcestershire',
  'liquid amino',
  'yondu',
  'vegetable oil',
];

export const GLUTEN_SYNONYMS = [
  'gluten',
  'wheat',
  'flour',
  'bread',
  'pasta',
  'noodle',
  'panko',
  'farro',
  'couscous',
  'orzo',
  'phyllo',
  'filo',
  'semolina',
  'durum',
  'barley',
  'rye',
  'malt',
  'spelt',
  'bulgur',
  'triticale',
  'udon',
  'ramen',
  'seitan',
  'breadcrumbs',
  'tempura',
  'batter',
  'cracker',
  'pretzel',
  'biscuit',
  'croissant',
  'pastry',
  'pie crust',
  'tortilla',
  'dumpling wrapper',
  'wonton wrapper',
  'ravioli',
  'macaroni',
  'spaghetti',
  'penne',
  'linguine',
  'fettuccine',
  'fettuccini',
  'fettucine',
  'rigatoni',
  'lasagna',
  'lasagne',
  'orecchiette',
  'fusilli',
  'rotini',
  'gnocchi',
  'tortellini',
  'tortelloni',
  'cannelloni',
  'manicotti',
  'tagliatelle',
  'pappardelle',
  'bucatini',
  'ziti',
  'farfalle',
  'cavatappi',
  'vermicelli',
  'crouton',
  'baguette',
  'dough',
  'pizza crust',
  'pancake',
  'waffle',
  'crepe',
  'focaccia',
  'ciabatta',
  'brioche',
  'challah',
  'scone',
  'doughnut',
  'donut',
  'roux',
  'gravy',
  'beer',
  'cereal',
  'cake',
  'muffin',
  'bun',
  'bagel',
  'pita',
  'naan',
  'soy sauce',
  'shoyu',
];

export const DAIRY_SYNONYMS = [
  ...DAIRY_PROTEIN_SYNONYMS,
  'dairy',
  'cream',
  'butter',
  'sour cream',
  'crema',
  'creme fraiche',
  'parmesan',
  'grana',
  'cheddar',
  'brie',
  'chevre',
  'mascarpone',
  'lactose',
  'ghee',
  'khoya',
  'mawa',
  'mava',
  'malai',
  'queso',
  'buttermilk',
  'provolone',
  'cotija',
  'gouda',
  'casein',
  'caseinate',
  'gruyere',
  'colby',
  'monterey jack',
  'pepper jack',
  'manchego',
  'gorgonzola',
  'camembert',
  'asiago',
  'romano',
  'quark',
  'custard',
  'flan',
  'ganache',
  'bechamel',
  'alfredo',
  'tzatziki',
  'raita',
  'lassi',
  'half and half',
  'half-and-half',
];

export const ONIONS_SYNONYMS = ['onion', 'onions', 'green onion', 'scallion'];

export const AVOCADO_SYNONYMS = ['avocado', 'avocados'];

export const SESAME_SYNONYMS = ['sesame', 'sesame oil', 'sesame seeds', 'tahini'];

export const BELL_PEPPER_SYNONYMS = ['bell pepper', 'bell peppers'];

export const GARLIC_SYNONYMS = ['garlic', 'garlic powder', 'roasted garlic'];

export const INGREDIENT_MISSPELLINGS: Partial<Record<FlaggableIngredient, string[]>> = {
  [FlaggableIngredient.Cilantro]: ['corriander'],
  [FlaggableIngredient.Eggs]: ['mayonaise', 'eggyolk'],
  [FlaggableIngredient.Soy]: ['edimame', 'liquid amios'],
  [FlaggableIngredient.Nuts]: ['alomond', 'waltnut'],
  [FlaggableIngredient.Mushrooms]: ['crimini'],
  [FlaggableIngredient.Gluten]: ['flouur', 'glueten'],
};

export const NOT_CILANTRO_LIST: string[] = [];
export const NOT_MUSHROOMS_LIST = ['mushroom powder', 'mushroom seasoning'];
export const NOT_NUTS_LIST = [
  'coconut',
  'butternut',
  'butter nut',
  'donut',
  'doughnut',
  'water chestnut',
  'waterchest nut',
];
export const NOT_SHELLFISH_LIST = [
  'seafood seasoning',
  'oyster mushroom',
  'king oyster',
  'lobster mushroom',
  'scallop mushroom',
  'oyster cracker',
  'mushroom oyster sauce',
  'vegetarian oyster',
  'vegan oyster',
];
export const NOT_EGGS_LIST = ['eggplant', 'eggless', 'ranch seasoning', 'ranch style'];
export const NOT_SOY_LIST = ['soy-free', 'soy free', 'no soy', 'chickpea miso', 'coconut amino'];
export const NATURALLY_GLUTEN_FREE_STARCHES = [
  'chickpea',
  'tapioca',
  'gram',
  'jowar',
  'sorghum',
  'rice',
  'almond',
  'coconut',
  'corn',
  'buckwheat',
  'sweet potato',
  'quinoa',
  'cassava',
  'teff',
  'corn masa',
  'besan',
  'maize',
  'yam',
  'ragi',
  'raagi',
  'yucca',
  'mandioca',
  'millet',
];

const NATURALLY_GLUTEN_FREE_STARCH_FORMS = [
  'flour',
  'noodle',
  'pasta',
  'tortilla',
  'wrapper',
  'macaroni',
  'spaghetti',
  'penne',
  'fusilli',
  'rotini',
  'vermicelli',
  'dough',
  'pancake',
  'waffle',
  'crepe',
];

const NATURALLY_GLUTEN_FREE_STARCH_TERMS = flatten(
  NATURALLY_GLUTEN_FREE_STARCHES.map((starch) => NATURALLY_GLUTEN_FREE_STARCH_FORMS.map((form) => `${starch} ${form}`))
);

export const NOT_GLUTEN_LIST = [
  'gluten-free',
  'gluten free',
  'glutenfree',
  ...NATURALLY_GLUTEN_FREE_STARCH_TERMS,
  'rice paper',
  'spaghetti squash',
  'tamari',
  'root beer',
  'ginger beer',
  'rice cake',
  'crab cake',
  'fish cake',
  'rice cracker',
  'corn cereal',
  'rice cereal',
  'glass noodle',
  'mung bean noodle',
  'clear vermicelli',
  'sev',
  'cannelloni bean',
  'donut peach',
  'donut peaches',
  'doughnut peach',
  'doughnut peaches',
];
export const NOT_DAIRY_LIST = [
  'dairy-free',
  'dairy free',
  'coconut milk',
  'almond milk',
  'oat milk',
  'soy milk',
  'coconut cream',
  'bean curd',
  'cashew cream',
  'cashew crema',
  'coconut crema',
  'balsamic crema',
  'crema di balsamico',
  'oat cream',
  'soy curd',
  'coconut yogurt',
  'almond yogurt',
  'soy yogurt',
  'oat yogurt',
  'cashew yogurt',
  'peanut butter',
  'almond butter',
  'cashew butter',
  'nut butter',
  'romano beans',
  'cocoa butter',
  'shea butter',
  'apple butter',
  'crab butter',
  'butter bean',
  'tofu curd',
  'palm butter',
  'olive oil butter',
  'soy cream',
  'cinnamon sunflower butter',
  'butter nut',
];
export const NOT_ONIONS_LIST: string[] = [];
export const NOT_AVOCADO_LIST: string[] = [];
export const NOT_SESAME_LIST: string[] = [];
export const NOT_BELL_PEPPER_LIST: string[] = [];
export const NOT_GARLIC_LIST: string[] = [];

/**
 * Animal-byproduct and split-category term lists. These are reference data for
 * Supply taxonomy sign-off and a future vegan/vegetarian validation path; they
 * are intentionally NOT part of FlaggableIngredient and NOT run through the
 * getFlaggableIngredients matcher yet.
 */
export const DAIRY_DERIVATIVES = ['whey', 'casein', 'caseinate', 'lactose', 'milk solids', 'milk powder', 'ghee'];
export const EGG_DERIVATIVES = ['albumin', 'albumen', 'ovalbumin', 'egg wash', 'meringue'];
export const TREE_NUT_SYNONYMS = [
  'almond',
  'cashew',
  'walnut',
  'pecan',
  'pistachio',
  'hazelnut',
  'macadamia',
  'chestnut',
];
export const PEANUT_SYNONYMS = ['peanut', 'groundnut'];

export const NON_VEGETARIAN_BYPRODUCTS = {
  renderedFats: ['lard', 'tallow', 'suet', 'schmaltz', 'duck fat', 'bacon fat', 'beef fat', 'chicken fat'],
  boneAndConnectiveTissue: [
    'gelatin',
    'gelatine',
    'bone marrow',
    'marrowbone',
    'collagen',
    'bone char',
    'rennet',
    'pepsin',
    'isinglass',
    'marshmallow',
  ],
  brothsStocksDrippings: [
    'broth',
    'stock',
    'bouillon',
    'soup base',
    'soup cube',
    'gravy',
    'consomme',
    'drippings',
    'casing',
    'chicken broth',
    'chicken stock',
    'beef broth',
    'beef stock',
    'pork stock',
    'bone broth',
    'chicken powder',
    'chicken base',
    'beef base',
    'demi glace',
    'demi-glace',
    'au jus',
  ],
  fishAndSeafoodSauces: [
    'fish sauce',
    'oyster sauce',
    'worcestershire sauce',
    'worcestershire',
    'anchovy paste',
    'dashi',
    'hondashi',
    'nam pla',
    'nuoc mam',
    'shrimp paste',
    'bagoong',
    'belacan',
  ],
};

export const NON_VEGAN_BYPRODUCTS = ['eggs', 'honey', 'beeswax'];

export const APIARY_PRODUCTS = ['honey', 'beeswax', 'royal jelly', 'bee pollen'];

export const DIET_VALIDATION_EXCLUSIONS = [
  'vegetarian',
  'vegetable',
  'vegan',
  'veggie',
  'plant based',
  'plant-based',
  'mushroom broth',
  'mushroom stock',
  'mushroom gravy',
  'mushroom bouillon',
  'agar agar',
  'agar-agar',
  'pectin',
  'honey-free',
  'honey free',
  'gelatin-free',
  'gelatin free',
];
